import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import { formatTwoDates, limitCharactersInText } from '../../../utils/global-utils'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import { RouteConstants } from '../../../navigation/navigation-types'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { PendingDemandDto } from '../../../services/api/sharedTypes/Dashboard'

const headCells: readonly HeadCell[] = [
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Klient',
    },
    {
        id: 'eventStartsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum',
    },
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'eventDescription',
        numeric: false,
        disablePadding: false,
        label: 'Popis',
    },
    {
        id: 'eventAddress',
        numeric: false,
        disablePadding: false,
        label: 'Místo',
    },
    {
        id: 'eventCreatedAt',
        numeric: false,
        disablePadding: false,
        label: 'Vytvořeno',
    }
]

interface PendingDemandsTableProps {
    pendingDemands: PendingDemandDto[]
}

const PendingDemandsTable: React.FC<PendingDemandsTableProps> = ({ pendingDemands }: PendingDemandsTableProps) => {
    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<string>('eventCreatedAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {pendingDemands.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={pendingDemands.length}
                        />
                        <TableBody>
                            {stableSort(
                                pendingDemands.map((row) => {
                                    return {
                                        ...row,
                                        eventIdName: row.eventIdName.name,
                                        eventName: row.eventIdName.name,
                                        eventId: row.eventIdName.id ?? '',
                                        clientIdName: row.clientIdName.name,
                                        clientName: row.clientIdName.name,
                                        clientId: row.clientIdName.id ?? '',
                                        eventAddress:
                                            row.eventAddress.city + ', ' + row.eventAddress.street + ' ' + row.eventAddress.zip,
                                        eventEndsAt: new Date(row.eventEndsAt).getTime(),
                                        eventStartsAt: new Date(row.eventStartsAt).getTime(),
                                        eventCreatedAt: new Date(row.eventCreatedAt).getTime(),
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow hover tabIndex={-1} key={index} style={{ cursor: 'pointer' }}>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(row.clientName, 25)}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ width: 270 }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {limitCharactersInText(
                                                    formatTwoDates(new Date(row.eventStartsAt), new Date(row.eventEndsAt)),
                                                    35
                                                )}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(row.eventName, 45)}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(row.eventDescription, 80)}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(row.eventAddress, 25)}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(
                                                    new Date(row.eventCreatedAt).toLocaleDateString('cs-CZ'),
                                                    12
                                                )}
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={pendingDemands.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default PendingDemandsTable
